import type { AppConfig } from '../config'
import type {
  AiRecipe,
  AiReportRequest,
  DeleteAccountResult,
  ExportResult,
  Food,
  FoodKind,
  ItemType,
  PlateRequest,
  PlateResult,
  ProgramAiRequest,
  ProgramAiResult,
  PublishedFile,
  PullPage,
  PushResult,
  RecipeImport,
  SyncItem,
  UploadKind,
  UploadTicket,
  ViewUrls,
} from './types'

export const MAX_PUSH_ITEMS = 100

export class ApiError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>

/** Where the client gets access tokens. The signed-in session (AuthManager) implements it. */
export interface TokenProvider {
  /** An access token with time left, refreshed first when it is about to expire. */
  getAccessToken(): Promise<string>
  /**
   * A newer token than [rejected], which the API just answered 401 to. Throws when the session cannot
   * be refreshed; when the refresh endpoint says the session is over, the user is already signed out.
   */
  refreshAccessToken(rejected: string): Promise<string>
  /** Ends the session because the API rejected even a freshly refreshed token. */
  signOut(): Promise<void>
}

/** Reads a JSON response body; an empty or non-JSON body reads as null. */
export async function readJsonBody(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** The people-facing message from an `{"error": "..."}` body, or a generic one naming the status. */
export function errorMessage(payload: unknown, status: number): string {
  return payload && typeof payload === 'object' && 'error' in payload && typeof payload.error === 'string'
    ? payload.error
    : `request failed (HTTP ${status})`
}

/** Talks to the two Lambda function URLs with the signed-in user's access token. */
export class ApiClient {
  private readonly apiUrl: string
  private readonly aiUrl: string
  private readonly tokens: TokenProvider
  private readonly fetchImpl: FetchLike

  constructor(config: Pick<AppConfig, 'apiUrl' | 'aiUrl'>, tokens: TokenProvider, fetchImpl?: FetchLike) {
    this.apiUrl = config.apiUrl
    this.aiUrl = config.aiUrl
    this.tokens = tokens
    this.fetchImpl = fetchImpl ?? ((input, init) => fetch(input, init))
  }

  async pull(options: { since?: number; cursor?: string | null; type?: ItemType } = {}): Promise<PullPage> {
    const query = new URLSearchParams({ since: String(options.since ?? 0) })
    if (options.cursor) query.set('cursor', options.cursor)
    if (options.type) query.set('type', options.type)
    return this.request<PullPage>(this.apiUrl, `/sync/pull?${query}`)
  }

  /** Follows the cursor until every item changed after [since] has been read. */
  async pullAll(options: { since?: number; type?: ItemType } = {}): Promise<{ items: SyncItem[]; since: number }> {
    const items: SyncItem[] = []
    let since = options.since ?? 0
    let cursor: string | null = null
    do {
      const page: PullPage = await this.pull({ since: options.since ?? 0, cursor, type: options.type })
      items.push(...page.items)
      since = Math.max(since, page.since)
      cursor = page.cursor
    } while (cursor)
    return { items, since }
  }

  /** Pushes in batches the API accepts and merges the per-batch results. */
  async push(items: ReadonlyArray<SyncItem<object>>): Promise<PushResult> {
    const result: PushResult = { accepted: [], rejected: [] }
    for (let start = 0; start < items.length; start += MAX_PUSH_ITEMS) {
      const batch = items.slice(start, start + MAX_PUSH_ITEMS)
      const response = await this.request<PushResult>(this.apiUrl, '/sync/push', {
        method: 'POST',
        body: JSON.stringify({ items: batch }),
      })
      result.accepted.push(...response.accepted)
      result.rejected.push(...response.rejected)
    }
    return result
  }

  async searchFoods(text: string, options: { kind?: FoodKind; limit?: number } = {}): Promise<Food[]> {
    const query = new URLSearchParams({ q: text, limit: String(options.limit ?? 20) })
    if (options.kind) query.set('kind', options.kind)
    const response = await this.request<{ results: Food[] }>(this.apiUrl, `/foods/search?${query}`)
    return response.results
  }

  async getFood(id: string): Promise<Food> {
    return this.request<Food>(this.apiUrl, `/foods/${encodeURIComponent(id)}`)
  }

  async createUpload(kind: UploadKind, contentType: string): Promise<UploadTicket> {
    return this.request<UploadTicket>(this.apiUrl, '/uploads', {
      method: 'POST',
      body: JSON.stringify({ kind, contentType }),
    })
  }

  /** Uploads a photo straight to S3 with a pre-signed URL and returns its key. */
  async uploadFile(kind: UploadKind, file: Blob): Promise<string> {
    const ticket = await this.createUpload(kind, file.type)
    const response = await this.fetchImpl(ticket.url, { method: 'PUT', headers: ticket.headers, body: file })
    if (!response.ok) {
      throw new ApiError(response.status, `upload failed (HTTP ${response.status})`)
    }
    return ticket.key
  }

  async importRecipeUrl(url: string): Promise<RecipeImport> {
    return this.request<RecipeImport>(this.apiUrl, '/recipes/import-url', { method: 'POST', body: JSON.stringify({ url }) })
  }

  /** The exercise library JSON: its version from the API, then the file from S3 (pre-signed, so no token). */
  async exerciseLibrary(): Promise<{ version: string; library: unknown }> {
    const latest = await this.request<PublishedFile>(this.apiUrl, '/exercises/latest')
    const response = await this.fetchImpl(latest.url)
    if (!response.ok) {
      throw new ApiError(response.status, `exercise library download failed (HTTP ${response.status})`)
    }
    return { version: latest.version, library: await readJsonBody(response) }
  }

  async exportData(): Promise<ExportResult> {
    return this.request<ExportResult>(this.apiUrl, '/export', { method: 'POST', body: '{}' })
  }

  /** Starts the 30-day deletion grace period; signing in again before [deleteAt] cancels it. */
  async deleteAccount(): Promise<DeleteAccountResult> {
    return this.request<DeleteAccountResult>(this.apiUrl, '/account', { method: 'DELETE' })
  }

  /** "Report this result" on an AI screen (settings privacy policy's AI reports). */
  async reportAiResult(payload: AiReportRequest): Promise<void> {
    await this.request(this.aiUrl, '/ai/report', { method: 'POST', body: JSON.stringify(payload) })
  }

  async plate(payload: PlateRequest): Promise<PlateResult> {
    return this.request<PlateResult>(this.aiUrl, '/ai/plate', { method: 'POST', body: JSON.stringify(payload) })
  }

  /** Reads a recipe pasted as text with the recipe prompt (D7); online only, like every AI route. */
  async recipeText(text: string): Promise<AiRecipe> {
    return this.request<AiRecipe>(this.aiUrl, '/ai/recipe-text', { method: 'POST', body: JSON.stringify({ text }) })
  }

  /** Adjusts a generated program from a plain-English request (setup's "Your training" step); online only, like every AI route. */
  async program(payload: ProgramAiRequest): Promise<ProgramAiResult> {
    return this.request<ProgramAiResult>(this.aiUrl, '/ai/program', { method: 'POST', body: JSON.stringify(payload) })
  }

  /** Short-lived signed links to the caller's own uploads (progress photos); at most 60 keys a request. */
  async viewUrls(keys: readonly string[]): Promise<ViewUrls> {
    return this.request<ViewUrls>(this.apiUrl, '/uploads/view', { method: 'POST', body: JSON.stringify({ keys }) })
  }

  /**
   * Sends an authorised request. A 401 gets one refresh and one retry (the token may have expired in
   * flight or been revoked); a second 401 means the session is no longer valid, so it signs out.
   * Bodies are always strings here, so the retry can resend them.
   */
  private async request<T>(base: string, path: string, init: RequestInit = {}): Promise<T> {
    const send = (token: string) => {
      const headers = new Headers(init.headers)
      headers.set('authorization', `Bearer ${token}`)
      if (init.body !== undefined) headers.set('content-type', 'application/json')
      return this.fetchImpl(`${base}${path}`, { ...init, headers })
    }

    const token = await this.tokens.getAccessToken()
    let response = await send(token)
    if (response.status === 401) {
      const fresh = await this.tokens.refreshAccessToken(token)
      response = await send(fresh)
      if (response.status === 401) await this.tokens.signOut()
    }

    const payload = await readJsonBody(response)
    if (!response.ok) {
      throw new ApiError(response.status, errorMessage(payload, response.status))
    }
    return payload as T
  }
}
